import React, { useState, useEffect } from "react";
import { Instagram, X, ChevronLeft, ChevronRight } from "lucide-react";
import { SITE } from "../lib/api";

const TILES = [
  { src: "/photos/projeto-acessibilidade.jpg", caption: "Programa Praia para Todos" },
  { src: "/photos/comunidade-pcd.jpg", caption: "Bate-papo com PCD" },
  { src: "/photos/cultura-capoeira.jpg", caption: "Cultura e capoeira" },
  { src: "/photos/comunidade-familia.jpg", caption: "Família laurofreitense" },
  { src: "/photos/juventude-esporte.jpg", caption: "Esporte e juventude" },
  { src: "/photos/manifestacao-paz.jpg", caption: "Paz é a única forma de sermos humanos" },
  { src: "/photos/reuniao-comunidade.jpg", caption: "Reunião com lideranças" },
  { src: "/photos/vistoria-projeto.jpg", caption: "Vistoria de projeto" },
  { src: "/photos/camara-microfone.jpg", caption: "Câmara Municipal" },
  { src: "/photos/orla-ipitanga.jpg", caption: "Nova Orla de Ipitanga · R$ 14,6 milhões" },
  { src: "/photos/trajetoria-camara.jpg", caption: "Na tribuna da Câmara" },
  { src: "/photos/represa-joanes.jpg", caption: "Represa do Rio Joanes" },
];

export default function InstagramGrid() {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const open = lightboxIdx !== null;
  const tile = open ? TILES[lightboxIdx] : null;

  const close = () => setLightboxIdx(null);
  const prev = () => setLightboxIdx((i) => (i - 1 + TILES.length) % TILES.length);
  const next = () => setLightboxIdx((i) => (i + 1) % TILES.length);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <section
      id="instagram"
      data-testid="instagram-section"
      className="py-24 md:py-32 bg-white border-t border-neutral-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#003F88] font-semibold">
              <span className="accent-line" />
              Galeria · {SITE.instagramHandle}
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 text-neutral-900">
              Acompanhe de <em className="italic font-light">perto</em>.
            </h2>
          </div>
          <a
            href={SITE.instagram}
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-cta"
            className="lg:col-span-5 lg:justify-self-end inline-flex items-center gap-3 bg-[#003F88] text-white px-6 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#002A5C] transition-colors w-fit"
          >
            <Instagram size={16} /> Seguir no Instagram
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-neutral-200">
          {TILES.map((t, idx) => (
            <button
              key={idx}
              onClick={() => setLightboxIdx(idx)}
              data-testid={`ig-tile-${idx}`}
              className="relative group bg-white aspect-square overflow-hidden text-left"
              title={t.caption}
            >
              <img
                src={t.src}
                alt={t.caption}
                className="editorial-img w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-[#003F88]/0 group-hover:bg-[#003F88]/85 transition-colors duration-300 flex items-end justify-start p-4">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-[#FFC72C] text-[10px] uppercase tracking-widest font-bold">Ver foto</span>
                  <div className="text-white text-xs md:text-sm mt-2 font-medium">{t.caption}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {open && tile && (
        <div
          data-testid="ig-lightbox"
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 md:p-10"
          onClick={close}
        >
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            data-testid="ig-lightbox-close"
            aria-label="Fechar"
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
          >
            <X size={24} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="ig-lightbox-prev"
            aria-label="Anterior"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2"
          >
            <ChevronLeft size={36} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="ig-lightbox-next"
            aria-label="Próximo"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2"
          >
            <ChevronRight size={36} />
          </button>
          <figure
            className="max-w-5xl max-h-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={tile.src}
              alt={tile.caption}
              className="max-h-[78vh] max-w-full object-contain"
            />
            <figcaption className="mt-4 text-white text-sm md:text-base text-center">
              {tile.caption}
              <span className="block mt-1 text-white/50 text-xs uppercase tracking-widest">
                {lightboxIdx + 1} / {TILES.length}
              </span>
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}
